/**
 * Oturum bağlamı — "kim girmiş" sorusunun tek cevabı.
 *
 * Neden bağlam: rol bilgisi hemen her ekranda gerekiyor (düğme gösterilecek mi,
 * kimlik numarası maskeli mi). Her ekranın kendi isteğini atması hem gereksiz
 * ağ trafiği hem de tutarsızlık kaynağı olurdu.
 *
 * Oturum durumu tarayıcıda SAKLANMAZ (localStorage yok): gerçek kaynak
 * sunucudaki httpOnly çerezdir. Sayfa yenilendiğinde `/api/auth/me` sorulur.
 * Böylece "tarayıcıda girişli görünüp sunucuda olmama" durumu oluşamaz.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, OturumHatasi, type Kullanici } from './lib/api';

interface OturumDurumu {
  /** İlk `me` çağrısı sürüyor — henüz giriş ekranı da gösterilmemeli. */
  yukleniyor: boolean;
  kullanici: Kullanici | null;
  ortam: string;
  canli: boolean;
  girisYap: (email: string, sifre: string, recaptchaToken?: string | null) => Promise<void>;
  cikisYap: () => Promise<void>;
  /** 401 alan herhangi bir istek bunu çağırır → giriş ekranına dönülür. */
  oturumBitti: () => void;
}

const Baglam = createContext<OturumDurumu | null>(null);

export function OturumSaglayici({ children }: { children: ReactNode }) {
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kullanici, setKullanici] = useState<Kullanici | null>(null);
  const [ortam, setOrtam] = useState('test');
  const [canli, setCanli] = useState(false);

  const sor = useCallback(async () => {
    try {
      const bilgi = await api.oturum();
      setKullanici(bilgi.user);
      setOrtam(bilgi.environment);
      setCanli(bilgi.isLive);
    } catch (hata) {
      // 401 beklenen durumdur (henüz girilmemiş); diğer hatalarda da giriş
      // ekranı gösterilir — orada hata mesajı kullanıcıya sunulur.
      if (!(hata instanceof OturumHatasi)) {
        // eslint-disable-next-line no-console -- açılışta sunucu erişilemezse iz kalsın
        console.error('Oturum sorgulanamadı', hata);
      }
      setKullanici(null);
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    void sor();
  }, [sor]);

  const girisYap = useCallback(
    async (email: string, sifre: string, recaptchaToken?: string | null) => {
      await api.girisYap(email, sifre, recaptchaToken);
      // Girişten sonra ortam bilgisini de almak için tam sorgu yapılır.
      await sor();
    },
    [sor]
  );

  const cikisYap = useCallback(async () => {
    try {
      await api.cikisYap();
    } finally {
      // Sunucu hata verse de yerelde çıkılmış sayılır: kullanıcı ekranda kalmasın.
      setKullanici(null);
    }
  }, []);

  const oturumBitti = useCallback(() => setKullanici(null), []);

  const deger = useMemo<OturumDurumu>(
    () => ({ yukleniyor, kullanici, ortam, canli, girisYap, cikisYap, oturumBitti }),
    [yukleniyor, kullanici, ortam, canli, girisYap, cikisYap, oturumBitti]
  );

  return <Baglam.Provider value={deger}>{children}</Baglam.Provider>;
}

export function useOturum(): OturumDurumu {
  const deger = useContext(Baglam);
  if (!deger) throw new Error('useOturum, OturumSaglayici içinde çağrılmalı');
  return deger;
}

/*
 * Rol sıralaması BURADA DEĞİL — artık rol sıralaması diye bir şey yok.
 * Yetki kontrolü İZİN üzerinden yapılır: src/lib/yetki.ts → `izinVar`.
 * Roller veritabanında ve kullanıcı kendi rolünü kurabiliyor; "hangi rol
 * daha yetkili" sorusunun sabit bir cevabı kalmadı.
 */
